import React, { useState } from 'react';
import { Typography, Button, Chip, Popover, Box, Avatar, AvatarGroup, Tooltip, IconButton } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import VideocamIcon from '@mui/icons-material/Videocam';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { API_BASE_URL } from 'config/api';
import {
    EventsContainer,
    EventsHeader,
    EventsGrid,
    EventCard,
    EventDateBox,
    EventContent,
    EventTitle,
    EventMeta,
    EventTypeBadge,
    EmptyEventsBox,
    AttendingBadge,
} from './styled';

const UpcomingEventsSectionView = ({ 
    events = [], 
    communityBookings = [],
    onEventClick,
    onBookingClick,
    onJoinBooking,
    onQuickBook,
    onViewAll,
    isAuthenticated = false,
}) => {
    // Popover state for Join action
    const [joinPopoverAnchor, setJoinPopoverAnchor] = useState(null);
    const [selectedBookingGroup, setSelectedBookingGroup] = useState(null);

    const formatTime = (dateString, timeString) => {
        if (timeString) {
            return dayjs(`2000-01-01 ${timeString}`).format('h:mm A');
        }
        return dayjs(dateString).format('h:mm A');
    };

    const formatDate = (dateString) => {
        const date = dayjs(dateString);
        return {
            day: date.format('D'),
            month: date.format('MMM'),
            weekday: date.format('ddd'),
        };
    };

    // Group bookings by date AND venue (so same date + same venue = 1 card with multiple attendees)
    const groupedBookings = communityBookings.reduce((acc, booking) => {
        const dateKey = booking.planned_start_day;
        const venueKey = booking.venue?.slug || booking.venue?.name || 'unknown';
        const key = `${dateKey}-${venueKey}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...booking,
                type: 'booking',
                allBookings: [booking],
                allParticipants: [],
                sortDate: `${booking.planned_start_day}T${booking.planned_start_time || '00:00:00'}`,
                totalParticipants: 0,
                canJoin: booking.can_join,
                totalAvailableSlots: booking.available_slots || 0,
                isOwner: booking.is_owner || false,
                isAttending: false, // Will be set below
            };
        } else {
            acc[key].allBookings.push(booking);
            // If any booking allows joining, the group allows joining
            if (booking.can_join) {
                acc[key].canJoin = true;
            }
            acc[key].totalAvailableSlots += (booking.available_slots || 0);
            // If any booking in the group is owned by user
            if (booking.is_owner) {
                acc[key].isOwner = true;
            }
        }
        
        // Collect all unique participants
        if (booking.participants) {
            booking.participants.forEach(p => {
                const exists = acc[key].allParticipants.find(
                    existing => existing.slug === p.slug
                );
                if (!exists) {
                    acc[key].allParticipants.push(p);
                }
            });
        }
        acc[key].totalParticipants = acc[key].allParticipants.length;
        
        // Check if user is attending (participant but not owner)
        // This is determined by is_owner being false but user appearing as a participant
        // Since we don't have direct access to current user ID here, we rely on backend flags
        // is_attending = user is part of the booking but didn't create it
        if (!acc[key].isOwner && booking.is_attending) {
            acc[key].isAttending = true;
        }
        
        return acc;
    }, {});

    // Combine events and grouped bookings into a unified list
    const combinedItems = [
        ...events.map(event => ({
            ...event,
            type: 'event',
            sortDate: event.start || event.start_date || event.date,
        })),
        ...Object.values(groupedBookings),
    ].sort((a, b) => new Date(a.sortDate) - new Date(b.sortDate));

    const handleJoinClick = (event, bookingGroup) => {
        event.stopPropagation();
        setJoinPopoverAnchor(event.currentTarget);
        setSelectedBookingGroup(bookingGroup);
    };

    const handleJoinClose = () => {
        setJoinPopoverAnchor(null);
        setSelectedBookingGroup(null);
    };

    const handleConfirmJoin = () => {
        if (selectedBookingGroup && onJoinBooking) {
            // Pass the first joinable booking
            const joinableBooking = selectedBookingGroup.allBookings.find(b => b.can_join) || selectedBookingGroup;
            onJoinBooking(joinableBooking);
        }
        handleJoinClose();
    };

    const handleQuickBook = (event, item) => {
        event.stopPropagation();
        if (onQuickBook) {
            onQuickBook(item);
        }
    };

    if (combinedItems.length === 0) {
        return (
            <EventsContainer>
                <EventsHeader>
                    <Typography variant="h6" fontWeight={600}>
                        Upcoming Events & Activities
                    </Typography>
                </EventsHeader>
                <EmptyEventsBox>
                    <EventIcon />
                    <Typography className="title">No upcoming events</Typography>
                    <Typography className="subtitle">
                        Check back later for community gatherings and activities
                    </Typography>
                </EmptyEventsBox>
            </EventsContainer>
        );
    }

    return (
        <EventsContainer>
            <EventsHeader>
                <Typography variant="h6" fontWeight={600}>
                    Upcoming Events & Activities
                </Typography>
                {combinedItems.length > 3 && (
                    <Button
                        variant="text"
                        endIcon={<ArrowForwardIcon />}
                        size="small"
                        onClick={onViewAll}
                        sx={{ 
                            textTransform: 'none',
                            color: 'primary.main',
                            fontWeight: 500,
                        }}
                    >
                        View All ({combinedItems.length})
                    </Button>
                )}
            </EventsHeader>

            <EventsGrid>
                {combinedItems.slice(0, 4).map((item, index) => {
                    if (item.type === 'event') {
                        const dateInfo = formatDate(item.start_date || item.date || item.start);
                        const isOnline = item.event_type === 'online' || item.is_online;

                        return (
                            <EventCard 
                                key={`event-${item.slug || item.id || index}`}
                                onClick={() => onEventClick?.(item)}
                            >
                                <EventDateBox>
                                    <span className="day">{dateInfo.day}</span>
                                    <span className="month">{dateInfo.month}</span>
                                    <span className="weekday">{dateInfo.weekday}</span>
                                    <span className="time">{formatTime(item.start_date || item.date || item.start)}</span>
                                </EventDateBox>

                                <EventContent>
                                    <EventTitle>
                                        {item.title || item.name}
                                    </EventTitle>

                                    <EventMeta>
                                        {isOnline ? (
                                            <div className="meta-item">
                                                <VideocamIcon />
                                                Online Event
                                            </div>
                                        ) : item.venue_name || item.location ? (
                                            <div className="meta-item">
                                                <PlaceIcon />
                                                {item.venue_name || item.location}
                                            </div>
                                        ) : null}
                                    </EventMeta>

                                    {item.event_type && (
                                        <EventTypeBadge eventType={isOnline ? 'online' : 'in-person'}>
                                            {isOnline ? 'Online' : 'In Person'}
                                        </EventTypeBadge>
                                    )}
                                </EventContent>
                            </EventCard>
                        );
                    } else {
                        // Community Booking (grouped by date + venue)
                        const dateInfo = formatDate(item.planned_start_day);
                        const bookingTypeLabels = {
                            'HD': 'Hot Desk',
                            'DS': 'Desk',
                            'MR': 'Meeting Room',
                            'TB': 'Table',
                            'ST': 'Suite',
                            'OS': 'Open Space',
                        };

                        return (
                            <EventCard 
                                key={`booking-${item.planned_start_day}-${item.venue?.slug || index}`}
                                onClick={() => onBookingClick?.(item)}
                            >
                                {/* Floating badge for owner, attending, or join */}
                                {(item.isOwner || item.isAttending || item.canJoin) && (
                                    <AttendingBadge 
                                        variant={item.isOwner ? 'owner' : item.isAttending ? 'attending' : 'join'}
                                        onClick={item.canJoin ? (e) => handleJoinClick(e, item) : undefined}
                                        sx={item.canJoin ? { cursor: 'pointer', '&:hover': { transform: 'scale(1.05)' } } : {}}
                                    >
                                        {item.isOwner ? 'Booked' : item.isAttending ? 'Attending' : 'Join'}
                                    </AttendingBadge>
                                )}

                                <EventDateBox>
                                    <span className="day">{dateInfo.day}</span>
                                    <span className="month">{dateInfo.month}</span>
                                    <span className="weekday">{dateInfo.weekday}</span>
                                    <span className="time">{formatTime(null, item.planned_start_time)}</span>
                                </EventDateBox>

                                <EventContent>
                                    <Tooltip title={item.venue?.name || 'Venue Booking'} arrow>
                                        <EventTitle>
                                            {item.venue?.name || 'Venue Booking'}
                                        </EventTitle>
                                    </Tooltip>

                                    <EventMeta>
                                        <div className="meta-item">
                                            <PlaceIcon />
                                            {bookingTypeLabels[item.booking_type] || 'Booking'}
                                            {item.price && parseFloat(item.price) > 0 && (
                                                <Chip 
                                                    label={`£${parseFloat(item.price).toFixed(2)}`}
                                                    size="small"
                                                    sx={{ 
                                                        height: '20px',
                                                        backgroundColor: '#e8f5e9',
                                                        color: '#2e7d32',
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        marginLeft: '6px',
                                                    }}
                                                />
                                            )}
                                        </div>

                                        {item.totalParticipants > 0 && (
                                            <div className="meta-item">
                                                <AvatarGroup 
                                                    max={5} 
                                                    sx={{ 
                                                        '& .MuiAvatar-root': { 
                                                            width: 20, 
                                                            height: 20, 
                                                            fontSize: '0.65rem',
                                                            border: '1px solid #fff'
                                                        } 
                                                    }}
                                                >
                                                    {item.allParticipants.map((p, idx) => (
                                                        <Tooltip key={idx} title={`${p.first_name} ${p.last_name}`}>
                                                            <Avatar 
                                                                src={p.avatar ? `${API_BASE_URL}${p.avatar}` : undefined}
                                                            >
                                                                {p.first_name?.[0]}
                                                            </Avatar>
                                                        </Tooltip>
                                                    ))}
                                                </AvatarGroup>
                                                <span style={{ marginLeft: '4px' }}>
                                                    {item.totalParticipants} {item.totalParticipants === 1 ? 'person' : 'people'}
                                                </span>
                                            </div>
                                        )}
                                    </EventMeta>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                        <EventTypeBadge eventType="community-booking">
                                            Community Booking
                                        </EventTypeBadge>
                                        
                                        {/* Quick Book Button */}
                                        <Tooltip title="Book at this venue">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleQuickBook(e, item)}
                                                sx={{
                                                    ml: 'auto',
                                                    p: 0.25,
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.light',
                                                        color: 'primary.dark',
                                                    },
                                                }}
                                            >
                                                <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </EventContent>
                            </EventCard>
                        );
                    }
                })}
            </EventsGrid>

            {/* Join Booking Popover */}
            <Popover
                open={Boolean(joinPopoverAnchor)}
                anchorEl={joinPopoverAnchor}
                onClose={handleJoinClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        p: 2,
                        maxWidth: 320,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    }
                }}
            >
                {selectedBookingGroup && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Join this booking
                            </Typography>
                            <IconButton size="small" onClick={handleJoinClose}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {selectedBookingGroup.venue?.name}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <EventIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                            <Typography variant="body2">
                                {dayjs(selectedBookingGroup.planned_start_day).format('dddd, MMM D')} at {formatTime(null, selectedBookingGroup.planned_start_time)}
                            </Typography>
                        </Box>

                        {selectedBookingGroup.totalParticipants > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Already attending:
                                </Typography>
                                <AvatarGroup 
                                    max={5}
                                    sx={{ 
                                        justifyContent: 'flex-start',
                                        '& .MuiAvatar-root': { 
                                            width: 28, 
                                            height: 28, 
                                            fontSize: '0.75rem' 
                                        } 
                                    }}
                                >
                                    {selectedBookingGroup.allParticipants.map((p, idx) => (
                                        <Tooltip key={idx} title={`${p.first_name} ${p.last_name}`}>
                                            <Avatar src={p.avatar ? `${API_BASE_URL}${p.avatar}` : undefined}>
                                                {p.first_name?.[0]}
                                            </Avatar>
                                        </Tooltip>
                                    ))}
                                </AvatarGroup>
                            </Box>
                        )}

                        {selectedBookingGroup.price && parseFloat(selectedBookingGroup.price) > 0 && (
                            <Box sx={{ 
                                p: 1.5, 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: 1, 
                                mb: 2 
                            }}>
                                <Typography variant="body2" fontWeight={500}>
                                    Price: £{parseFloat(selectedBookingGroup.price).toFixed(2)}
                                </Typography>
                                {selectedBookingGroup.vat_amount && (
                                    <Typography variant="caption" color="text.secondary">
                                        Includes £{parseFloat(selectedBookingGroup.vat_amount).toFixed(2)} VAT
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleConfirmJoin}
                            startIcon={<PersonAddIcon />}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: '#FFC107',
                                color: '#000',
                                fontWeight: 600,
                                '&:hover': {
                                    backgroundColor: '#FFB300',
                                },
                            }}
                        >
                            Confirm & Join
                        </Button>
                    </Box>
                )}
            </Popover>
        </EventsContainer>
    );
};

export default UpcomingEventsSectionView;
